// PushNotificationsPage — /settings/push.
//
// Lets a logged-in staff user enable / disable web-push notifications on
// the current browser. Wires the existing usePushNotifications stub by
// re-using its underlying flow (service-worker registration, VAPID key,
// `pushManager.subscribe`) and persisting the subscription via
// POST /api/v1/devices.

import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, BellOff } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useToast } from '../../../hooks/useToast';
import {
  isPushSupported,
  serialiseSubscription,
  subscribePush,
  unsubscribePush,
  useMyDevices,
  usePushPermission,
  useRegisterDevice,
  useUnregisterDevice,
} from './usePushSettings';
import styles from './PushNotificationsPage.module.css';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB');
}

export function PushNotificationsPage(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const supported = isPushSupported();
  const permission = usePushPermission();
  const devices = useMyDevices();
  const register = useRegisterDevice();
  const unregister = useUnregisterDevice();

  const activeDevices = (devices.data ?? []).filter((d) => d.is_active);

  async function handleEnable() {
    if (!supported) return;
    try {
      const sub = await subscribePush();
      if (!sub) {
        toast.error(
          'Could not subscribe to push notifications. Check that browser permissions are granted and the VAPID key is configured.',
        );
        return;
      }
      register.mutate({
        endpoint: sub.endpoint,
        keys: sub.keys,
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      });
    } catch {
      toast.error('Failed to enable push notifications.');
    }
  }

  async function handleDisable() {
    if (!supported) return;
    try {
      // Revoke the current browser's subscription. `unsubscribePush` returns
      // the endpoint URL we then post to DELETE so the server drops its row.
      const endpoint = await unsubscribePush();
      // If the browser had no subscription locally we still want to revoke
      // the most recent device record, so fall through to the server call.
      if (!endpoint) {
        toast.info('No local subscription found. Already disabled.');
        return;
      }
      unregister.mutate(endpoint);
    } catch {
      toast.error('Failed to disable push notifications.');
    }
  }

  async function getCurrentBrowserEndpoint(): Promise<string | null> {
    if (!supported) return null;
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    const sub = await reg.pushManager.getSubscription();
    return sub ? serialiseSubscription(sub).endpoint : null;
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Push Notifications"
        subtitle="Browser-based push alerts for booking, receipt and BACS events"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/settings')}
          >
            Back
          </Button>
        }
      />

      {!supported ? (
        <Card>
          <p className={styles.warn}>
            This browser does not support service workers or the Push API. Open
            the app in Chrome, Edge or Firefox on desktop to enable push
            notifications.
          </p>
        </Card>
      ) : null}

      {supported ? (
        <Card title="This browser">
          <p className={styles.row}>
            <strong>Status:</strong>
            <span>
              {permission === 'granted'
                ? 'Permission granted'
                : permission === 'denied'
                  ? 'Permission denied (reset in browser settings)'
                  : 'Not enabled yet'}
            </span>
          </p>
          <p className={styles.row}>
            <strong>VAPID public key:</strong>
            <span>
              {import.meta.env.VITE_VAPID_PUBLIC_KEY
                ? 'Configured'
                : 'Missing — set VITE_VAPID_PUBLIC_KEY'}
            </span>
          </p>
          <div className={styles.actions}>
            <Button
              variant="primary"
              icon={Bell}
              onClick={handleEnable}
              loading={register.isPending}
              disabled={!supported || permission === 'denied'}
            >
              Enable push on this browser
            </Button>
            <Button
              variant="secondary"
              icon={BellOff}
              onClick={handleDisable}
              loading={unregister.isPending}
              disabled={!supported}
            >
              Disable on this browser
            </Button>
          </div>
        </Card>
      ) : null}

      <Card title={`Registered devices (${activeDevices.length})`} flush>
        {devices.isLoading ? (
          <div className={styles.empty}>
            <Spinner />
          </div>
        ) : devices.data && devices.data.length === 0 ? (
          <p className={styles.empty}>
            No devices registered yet. Click "Enable push" above to add this
            browser.
          </p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Device</th>
                  <th>Active</th>
                  <th>Last used</th>
                  <th>Registered</th>
                </tr>
              </thead>
              <tbody>
                {(devices.data ?? []).map((d) => (
                  <tr key={d.uuid}>
                    <td>{d.platform}</td>
                    <td className={styles.device}>{d.device_info ?? '—'}</td>
                    <td>{d.is_active ? 'Yes' : 'No'}</td>
                    <td>{formatDate(d.last_used_at)}</td>
                    <td>{formatDate(d.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className={styles.footnote} aria-hidden>
        {/* Reference helper to keep import used; surface for debugging. */}
        <span style={{ display: 'none' }}>
          {String(getCurrentBrowserEndpoint)}
        </span>
      </p>
    </div>
  );
}
